import React, { useState, useEffect } from 'react';
import { BarChart3, Zap, Clock, Database, Globe, AlertTriangle, CheckCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { PerformanceMonitor, getMemoryUsage, analyzeBundleSize } from '../utils/performance';
import { ErrorHandler } from '../utils/errorHandling';
import { runStripeTestSuite, validateTestEnvironment } from '../utils/stripeTestSuite';

interface PerformanceData {
  pageLoadTime: number;
  apiResponseTimes: Record<string, number>;
  memoryUsage: any;
  bundleSize: any;
  errorStats: any;
}

export const PerformanceDashboard: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [stripeTestResults, setStripeTestResults] = useState<any>(null);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [activeTab, setActiveTab] = useState<'performance' | 'errors' | 'stripe'>('performance');

  useEffect(() => {
    collectPerformanceData();
  }, []);

  const collectPerformanceData = () => {
    const monitor = new PerformanceMonitor();
    const metrics = monitor.getMetrics();
    const memoryUsage = getMemoryUsage();
    const bundleSize = analyzeBundleSize();
    const errorHandler = ErrorHandler.getInstance();
    const errorStats = errorHandler.getErrorStats();

    setPerformanceData({
      pageLoadTime: metrics.pageLoadTime || 0,
      apiResponseTimes: metrics.apiResponseTimes || {},
      memoryUsage,
      bundleSize,
      errorStats
    });
  };

  const runStripeTests = async () => {
    if (!validateTestEnvironment()) {
      alert('Test environment not properly configured');
      return;
    }

    setIsRunningTests(true);
    try {
      const results = await runStripeTestSuite();
      setStripeTestResults(results);
    } catch (error) {
      console.error('Error running Stripe tests:', error);
    } finally {
      setIsRunningTests(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getPerformanceScore = (value: number, thresholds: { good: number; poor: number }): 'good' | 'needs-improvement' | 'poor' => {
    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.poor) return 'needs-improvement';
    return 'poor';
  };

  const getScoreColor = (score: string): string => {
    switch (score) {
      case 'good': return 'text-green-600 bg-green-100';
      case 'needs-improvement': return 'text-yellow-600 bg-yellow-100';
      case 'poor': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-96 max-h-96 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex items-center space-x-2">
            <BarChart3 size={20} />
            <h3 className="font-semibold">Performance Dashboard</h3>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('performance')}
            className={`flex-1 py-2 px-4 text-sm font-medium ${
              activeTab === 'performance' 
                ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Performance
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`flex-1 py-2 px-4 text-sm font-medium ${
              activeTab === 'errors' 
                ? 'bg-red-50 text-red-600 border-b-2 border-red-600' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Errors
          </button>
          <button
            onClick={() => setActiveTab('stripe')}
            className={`flex-1 py-2 px-4 text-sm font-medium ${
              activeTab === 'stripe' 
                ? 'bg-purple-50 text-purple-600 border-b-2 border-purple-600' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Stripe
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-64 overflow-y-auto">
          {/* Performance Tab */}
          {activeTab === 'performance' && performanceData && (
            <div className="space-y-4">
              {/* Page Load Time */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock size={16} className="text-blue-600" />
                  <span className="text-sm">Page Load</span>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  getScoreColor(getPerformanceScore(performanceData.pageLoadTime, { good: 1000, poor: 3000 }))
                }`}>
                  {performanceData.pageLoadTime.toFixed(0)}ms
                </div>
              </div>

              {/* Memory Usage */}
              {performanceData.memoryUsage && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Database size={16} className="text-green-600" />
                    <span className="text-sm">Memory</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {formatBytes(performanceData.memoryUsage.usedJSHeapSize)}
                  </div>
                </div>
              )}

              {/* Bundle Size */}
              {performanceData.bundleSize && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Globe size={16} className="text-purple-600" />
                    <span className="text-sm">Bundle</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {formatBytes(performanceData.bundleSize.transferSize)}
                  </div>
                </div>
              )}

              {/* API Response Times */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">API Response Times</h4>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {Object.entries(performanceData.apiResponseTimes).map(([url, time]) => (
                    <div key={url} className="flex justify-between text-xs">
                      <span className="truncate flex-1 mr-2">{url.split('/').pop()}</span>
                      <span className={`font-medium ${
                        time < 500 ? 'text-green-600' : time < 1000 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {time.toFixed(0)}ms
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={collectPerformanceData}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Refresh Metrics
              </button>
            </div>
          )}

          {/* Errors Tab */}
          {activeTab === 'errors' && performanceData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {performanceData.errorStats.total}
                  </div>
                  <div className="text-xs text-gray-600">Total Errors</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">
                    {performanceData.errorStats.bySeverity?.critical || 0}
                  </div>
                  <div className="text-xs text-gray-600">Critical</div>
                </div>
              </div>

              {/* Error by Category */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">By Category</h4>
                <div className="space-y-1">
                  {Object.entries(performanceData.errorStats.byCategory || {}).map(([category, count]) => (
                    <div key={category} className="flex justify-between text-xs">
                      <span className="capitalize">{category}</span>
                      <span className="font-medium">{count as number}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Errors */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Errors</h4>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {performanceData.errorStats.recent?.map((error: any, index: number) => (
                    <div key={index} className="text-xs text-gray-600 truncate">
                      {error.error?.message || 'Unknown error'}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => {
                  ErrorHandler.getInstance().clearErrorQueue();
                  collectPerformanceData();
                }}
                className="w-full bg-red-600 text-white py-2 px-4 rounded text-sm hover:bg-red-700 transition-colors"
              >
                Clear Error Log
              </button>
            </div>
          )}

          {/* Stripe Tab */}
          {activeTab === 'stripe' && (
            <div className="space-y-4">
              <button
                onClick={runStripeTests}
                disabled={isRunningTests}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isRunningTests ? 'Running Tests...' : 'Run Stripe Tests'}
              </button>

              {stripeTestResults && (
                <div className="space-y-3">
                  {/* Integration Tests */}
                  <div className="bg-gray-50 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Integration Tests</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        stripeTestResults.integrationTests.failedTests === 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {stripeTestResults.integrationTests.passedTests}/{stripeTestResults.integrationTests.totalTests}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Duration: {stripeTestResults.integrationTests.totalDuration.toFixed(0)}ms
                    </div>
                  </div>

                  {/* Payment Tests */}
                  <div className="bg-gray-50 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Payment Tests</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        stripeTestResults.paymentTests.failedTests === 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {stripeTestResults.paymentTests.passedTests}/{stripeTestResults.paymentTests.totalTests}
                      </span>
                    </div>
                  </div>

                  {/* Security Tests */}
                  <div className="bg-gray-50 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Security Tests</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        stripeTestResults.securityTests.failedTests === 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {stripeTestResults.securityTests.passedTests}/{stripeTestResults.securityTests.totalTests}
                      </span>
                    </div>
                  </div>

                  {/* Benchmarks */}
                  <div className="bg-gray-50 rounded p-3">
                    <h4 className="text-sm font-medium mb-2">Benchmarks</h4>
                    <div className="space-y-1">
                      {Object.entries(stripeTestResults.benchmarks).map(([operation, time]) => (
                        <div key={operation} className="flex justify-between text-xs">
                          <span className="capitalize">{operation.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-medium">{(time as number).toFixed(0)}ms</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};